import decode from 'jwt-decode';
import axios from "axios";
import http from "../components/config/axiosContextConfig";

const httpClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

httpClient.interceptors.request.use(function (config) {
  const token = getLocalToken();
  config.headers.Authorization =  token ? `Bearer ${token}` : '';
  return config;
});

const POST_LOGIN_PENDING_KEY = 'post_login_pending';

const isAuthenticated = () => {
  try {
    const decodedToken = decode(getLocalToken())
    if (isTokenValid(decodedToken) && !isTokenExpired(decodedToken)) {
      return true
    }
  } catch (e) {
    console.log(e.message)
  }
  removeToken()
  return false
}

const storeAuthorizationToken = (token, hadPriorToken) => {
  // Callers should capture whether a token was present BEFORE any
  // isAuthenticated() check ran, since isAuthenticated() has the side effect
  // of removing expired tokens - which would otherwise make a silent
  // session-refresh (token self-extension) indistinguishable from a
  // brand-new login by the time we get here.
  const priorTokenExisted = typeof hadPriorToken === 'boolean' ? hadPriorToken : !!isTokenExistInLocalStorage()
  const postLoginPending = isPostLoginPending()
  try {
    const decodedToken = decode(token)
    if (isTokenValid(decodedToken) && !isTokenExpired(decodedToken)) {
      setLocalToken(token)
    }
  } catch (e) {
    console.log(e.message)
  }
  // Call post-login for a brand-new session (no token existed prior to this
  // login attempt) or when a manual logout explicitly requested that the
  // next successful login runs post-login again (e.g. to reset the MFA
  // verified flag). A silent token self-extension (a token already existed,
  // whether still valid or merely expired) intentionally does NOT call
  // post-login/MFA reset.
  // IMPORTANT: this returns a Promise that resolves only once post-login
  // (when triggered) has actually completed server-side. Callers MUST wait
  // on it before fetching user details/privileges - otherwise the user
  // fetch can race ahead of the server-side MFA reset and return stale
  // mfaVerified state, incorrectly skipping the MFA screen.
  if (!priorTokenExisted || postLoginPending) {
    clearPostLoginPending()
    return postLogin().then(() => {
      console.log("Reset MFA Flag new login")
    })
  }
  return Promise.resolve()
}

const isTokenExpired = (token) => {
  if (process.env.REACT_APP_API_BASE_URL === "http://localhost:8080/api/v1") {
    return false
  }
  return token.exp < Date.now() / 1000;
}

const isTokenValid = (token) => {
  return token.iss === process.env.REACT_APP_ATLAS_AMBASSADOR_URL && token.aud[0] === "https://ptc-services"
}

const setLocalToken = (idToken) => {
  localStorage.setItem('id_token', idToken)
}

const getLocalToken = () => {
  return localStorage.getItem('id_token')
}

const isTokenExistInLocalStorage = () => {
  return localStorage.getItem('id_token')
}

const removeToken = () => {
  localStorage.removeItem('id_token');
}

const getToken = () => {
  if (!isAuthenticated()) {
    return null;
  }
  return decode(getLocalToken());
}

const getEmail = () => {
  let email = null;
  let token = getToken();

  if (!isAuthenticated()) {
    return null;
  }
  if (token && token.hasOwnProperty("https://ptc-services.com/email")) {
    email = token['https://ptc-services.com/email'];
  }
  return email;
}

const removeIdToken = () => {
  localStorage.removeItem('id_token');
}

// Marks that the user explicitly logged out, so that whenever the next
// successful login completes, post-login is guaranteed to run again
// (e.g. to reset MFA verification), regardless of any stray token state.
const markManualLogout = () => {
  localStorage.setItem(POST_LOGIN_PENDING_KEY, 'true')
}

const isPostLoginPending = () => {
  return localStorage.getItem(POST_LOGIN_PENDING_KEY) === 'true'
}

const clearPostLoginPending = () => {
  localStorage.removeItem(POST_LOGIN_PENDING_KEY)
}

const postLogin = () => {
  return http.post('/post-login')
}

export default {
  isAuthenticated,
  isTokenExpired,
  storeAuthorizationToken,
  getLocalToken,
  removeToken,
  getToken,
  getEmail,
  isTokenExistInLocalStorage,
  removeIdToken,
  markManualLogout,
  isPostLoginPending,
  postLogin
};
